import React, { createContext, type ReactNode, useContext, useMemo } from 'react';

import { type ConversationAssistantProps } from '@atlassian/conversation-assistant/types';

import type { AISmartButtonProps } from '../types/shared-types';

/**
 * Context for sharing commonly-used props throughout the AISmartButton component tree.
 * This reduces prop drilling for shared configuration and callbacks.
 */
type AISmartButtonContextValue = Omit<ConversationAssistantProps, 'config'> & AISmartButtonProps;

const AISmartButtonContext = createContext<AISmartButtonContextValue | undefined>(undefined);

type AISmartButtonProviderProps = Omit<ConversationAssistantProps, 'config'> &
	AISmartButtonProps & {
		children: ReactNode;
	};

/**
 * Provider component that wraps the AISmartButton tree and provides shared values.
 *
 * NOTE: This component uses rest operator to automatically capture all props (including future ones
 * added to ConversationAssistantProps). This ensures that any new props
 * are automatically forwarded to AIMiniModal without manual updates needed here.
 */
export const AISmartButtonProvider = ({
	children,
	...props
}: AISmartButtonProviderProps): React.JSX.Element => {
	// We have to use Object.values to compare prop values instead of the props object reference,
	// which changes on every render due to the rest operator. Otherwise, the useMemo would be ineffective
	// since it compares object references, not their contents.
	const contextValue = useMemo(
		() => props,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		Object.values(props),
	);

	return (
		<AISmartButtonContext.Provider value={contextValue}>{children}</AISmartButtonContext.Provider>
	);
};

/**
 * Hook to consume AISmartButtonContext values.
 * Throws an error if used outside of AISmartButtonProvider.
 *
 * @returns {AISmartButtonContextValue} The context value containing all AISmartButton configuration and shared props
 * @throws {Error} If called outside of AISmartButtonProvider
 */
export const useAISmartButtonContext = (): AISmartButtonContextValue => {
	const context = useContext(AISmartButtonContext);
	if (!context) {
		throw new Error('useAISmartButtonContext must be used within an AISmartButtonProvider');
	}
	return context;
};
